import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

import { Carousel } from '../input/Carousel';
import type { GrabberHub } from '../input/Grabbers';
import type { ModeRegistry } from '../input/ModeDefinition';
import type { Mode, ToolMode } from './ToolMode';

/**
 * "Dominant grip: mode ring appears" — plan/mode-hierarchy.md's own bindings table. Shown for
 * as long as the dominant grip is held, presenting every mode from `ModeRegistry` as a
 * `Carousel` (`input/Carousel.ts`) above the dominant hand with the current one in front — the
 * owner's own instruction, after a headset session: "we need to standardize on the carousel menu
 * we have for shape categories... Make sure to reuse the label style from the shape selector,
 * that should be the way we do it everywhere. It's probably best to extract a common component
 * there so it doesn't drift" (plan/ui-improvements.md). This used to be a straight row of four
 * plates, built by hand through `PaletteUI.buildLabelledPlate`; it is now the same `Carousel`
 * the tray's own shape-category wheel uses, with the same arc, the same "front item is the one"
 * highlight, and the same label plate underneath it — not a second, visually-similar
 * implementation of any of that.
 *
 * **`snapTo`, not `goTo`+`tick`.** The mode-switch gesture (grip + stick flick,
 * `AuthoringController.tickToolCycle`) is the thing that actually decides which mode is current;
 * this carousel only ever mirrors that decision after the fact. Easing its own copy of the wheel
 * toward the new mode on top of the gesture that already happened would read as display lag on
 * the same input, not a second, independently worth-watching motion — so this snaps straight to
 * the new mode the instant `toolMode.mode` changes, the same choice `PaletteUI`'s own colour
 * readout carousel makes for the identical reason (`Carousel.ts`'s own doc has the fuller
 * account). The tray's category wheel is the one carousel in this project that *does* ease
 * (`goTo`/`stepBy` plus `tick`), because there the stick flick and the wheel are the same
 * gesture, not two steps removed from each other.
 *
 * One mesh per mode, built once at construction — never rebuilt or re-textured per tick, the
 * same "created once" discipline `Carousel`'s own tray consumer already follows. The current
 * mode reads as "the one" by construction: `Carousel`'s own front-item highlight (bigger,
 * slightly forward) is what a wheel already does for whichever item is active, with nothing
 * mode-ring-specific layered on top of it.
 */

/** Item plate size — a touch wider than the tray's own category plates (`Carousel`'s default
 *  caller in `PaletteUI.ts` uses 0.1 x 0.05), since a mode's name reads a little longer than a
 *  category's ("Build", "Draw", "Scan", "Link" vs. "shapes"). */
const PLATE_WIDTH = 0.16;
const PLATE_HEIGHT = 0.055;

const HEIGHT_OFFSET = 0.12;

const BACKGROUND = '#12151aE6';
const INK = '#E7ECF3';

export class ModeRingRenderer {
  /** World-anchored, never parented — repositioned and re-yawed onto the dominant hand every
   *  tick it is shown; the carousel's own meshes are parented to this, so their local arc
   *  positions (from `Carousel`'s own wheel geometry) compose with this anchor's world pose for
   *  free, the same way the tray's own category wheel composes under `PaletteUI`'s hand anchor. */
  private readonly anchor: TransformNode;
  private readonly carousel: Carousel;

  /** Which modes to show, and in what order — `toolMode.availableModes`, not a hardcoded four-
   *  value list: with no whiteboard in the room, `draw` and `scan` are absent from that list
   *  (`ToolMode.availableModes`'s own doc comment, `BOARD_DEPENDENT_MODES`), and this ring showing
   *  a mode `cycleTool` can never actually reach would be exactly the "selectable and does
   *  nothing" bug SPEC-190 exists to avoid (CLAUDE.md) — reachable one way (the mode-switch
   *  gesture) and offered a second (this ring) have to agree, and reading the one list `ToolMode`
   *  itself already computes is what keeps them from drifting apart by hand. */
  private readonly order: readonly Mode[];

  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
    private readonly toolMode: ToolMode,
    modes: ModeRegistry,
  ) {
    this.anchor = new TransformNode('mode-ring-anchor', scene);
    this.anchor.setEnabled(false);
    this.order = toolMode.availableModes;

    this.carousel = new Carousel(scene, this.anchor, {
      itemWidth: PLATE_WIDTH,
      itemHeight: PLATE_HEIGHT,
      ink: INK,
      meshNamePrefix: 'mode-ring',
    });

    this.carousel.setItems(
      this.order.map((mode) => ({ key: mode, label: modes[mode].label, color: BACKGROUND })),
    );
  }

  /** Whether the ring is currently shown — exposed for tests. */
  get isVisible(): boolean {
    return this.anchor.isEnabled();
  }

  /** The carousel this class renders through — exposed so a test can confirm this is the same
   *  `Carousel` component the tray's category wheel and the colour readout both use, structurally,
   *  rather than merely a visually similar row of plates. */
  get modeCarousel(): Carousel {
    return this.carousel;
  }

  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick());
    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  private tick(): void {
    const dominant = this.grabbers.byRole('dominant');
    const held = dominant?.near.pressed === true;

    if (!dominant || !held) {
      this.anchor.setEnabled(false);
      return;
    }

    this.anchor.setEnabled(true);

    if (this.carousel.activeItem?.key !== this.toolMode.mode) {
      this.carousel.snapTo(this.toolMode.mode);
    }

    this.anchor.position.copyFrom(dominant.position.add(new Vector3(0, HEIGHT_OFFSET, 0)));

    // Yaw-billboarded toward the eye, the same construction as ToolSwitchLabel/LabelRenderer —
    // -Z front face, so the yaw is atan2(-x, -z), not atan2(x, z) (CLAUDE.md). Falls back to
    // identity (facing world +Z) with no active camera — visibility and highlight must not
    // depend on a camera existing at all, only orientation does; a test driving this class
    // headlessly, with no camera constructed, is still a legitimate way to prove "shown while
    // gripped" without needing to also prove billboard orientation in the same assertion.
    const camera = this.scene.activeCamera;
    const anchorPosition = this.anchor.position;
    const rotation = camera
      ? Quaternion.FromEulerAngles(
          0,
          Math.atan2(-(camera.globalPosition.x - anchorPosition.x), -(camera.globalPosition.z - anchorPosition.z)),
          0,
        )
      : Quaternion.Identity();

    this.anchor.rotationQuaternion = rotation;
  }

  dispose(): void {
    this.carousel.dispose();
    this.anchor.dispose();
  }
}
